/**
 * V168 — Connected Stores Dashboard Tab
 *
 * Lets the user link external WordPress / Shopify shops to their account
 * so Blog Agent articles get auto-published to those storefronts.
 *
 * Flow:
 *   1. Click "Add Store" → fill platform + URL (+ Shopify token).
 *   2. For WordPress: copy the generated plugin_api_key into TopRankPulse
 *      WP Plugin → Settings → Blog Agent.
 *   3. For Shopify: we push articles directly via Admin API.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Store, ShoppingCart, Globe, Plus, Trash2, Copy, Check, Loader2,
  PlugZap, RefreshCw, AlertTriangle, CheckCircle2, X, TrendingUp, Link2,
} from "lucide-react";
import { API } from "../../config";

const ALL_LANGS = [
  ["sk", "🇸🇰 SK"], ["en", "🇬🇧 EN"], ["cs", "🇨🇿 CS"], ["de", "🇩🇪 DE"],
  ["pl", "🇵🇱 PL"], ["hu", "🇭🇺 HU"], ["fr", "🇫🇷 FR"], ["es", "🇪🇸 ES"],
  ["it", "🇮🇹 IT"], ["nl", "🇳🇱 NL"],
];

export default function ConnectedStoresTab({ user, t = {} }) {
  const email = (user?.email || "").toLowerCase();
  const [stores, setStores] = useState([]);
  const [reach, setReach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState({});

  const load = async () => {
    if (!email) { setLoading(false); return; }
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/connected-stores`, { params: { email } }),
        axios.get(`${API}/connected-stores/total-reach`, { params: { email } }),
      ]);
      setStores(r1.data.stores || []);
      setReach(r2.data || null);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  const remove = async (store_id) => {
    if (!window.confirm(t.cs_confirm_delete || "Odpojiť tento obchod?")) return;
    await axios.delete(`${API}/connected-stores/${store_id}`);
    await load();
  };

  const togglePause = async (s) => {
    const next = s.status === "active" ? "paused" : "active";
    await axios.patch(`${API}/connected-stores/${s.store_id}`, { status: next });
    await load();
  };

  const rotateKey = async (store_id) => {
    if (!window.confirm(t.cs_confirm_rotate || "Vygenerovať nový API kľúč? Starý prestane fungovať.")) return;
    await axios.patch(`${API}/connected-stores/${store_id}`, { rotate_key: true });
    await load();
  };

  const testConn = async (store_id) => {
    setTesting(store_id);
    try {
      const r = await axios.post(`${API}/connected-stores/${store_id}/test`);
      setTestResult({ ...testResult, [store_id]: r.data });
    } catch (e) {
      setTestResult({ ...testResult, [store_id]: { ok: false, error: e?.response?.data?.detail || "error" } });
    } finally {
      setTesting(null);
    }
  };

  const copy = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-gray-500 p-6" data-testid="cs-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>{t.cs_loading || "Načítavam pripojené obchody…"}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="connected-stores-tab">
      {/* Total Reach widget */}
      {reach && (
        <div className="bg-gradient-to-br from-violet-600 via-fuchsia-600 to-purple-700 text-white rounded-xl p-5 shadow-lg" data-testid="cs-total-reach">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white/15 backdrop-blur rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">{t.cs_total_reach || "Total Reach"}</p>
              <p className="text-3xl font-black font-['Chivo']" data-testid="cs-reach-total">
                {reach.displayed_total.toLocaleString("sk-SK")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-white/15">
            <div>
              <p className="text-xl font-black font-['Chivo']" data-testid="cs-reach-stores">{reach.stores_count}</p>
              <p className="text-[10px] opacity-80 uppercase tracking-wider">{t.cs_reach_stores || "Obchody"}</p>
            </div>
            <div>
              <p className="text-xl font-black font-['Chivo']" data-testid="cs-reach-30d">+{reach.real_last_30d}</p>
              <p className="text-[10px] opacity-80 uppercase tracking-wider">{t.cs_reach_30d || "Posledných 30d"}</p>
            </div>
            <div>
              <p className="text-xl font-black font-['Chivo'] flex items-center justify-center gap-1" data-testid="cs-reach-backlinks">
                <Link2 className="w-4 h-4" />{reach.estimated_backlinks}
              </p>
              <p className="text-[10px] opacity-80 uppercase tracking-wider">{t.cs_reach_backlinks || "Backlinks"}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-black text-gray-800 font-['Chivo'] flex items-center gap-2">
            <PlugZap className="w-5 h-5 text-violet-600" />
            {t.cs_title || "Pripojené obchody"}
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {t.cs_subtitle || "Auto-publikuj AI články z Blog Agentov priamo do svojho WordPress / Shopify obchodu."}
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold px-4 py-2 rounded-lg"
          data-testid="cs-add-btn"
        >
          <Plus className="w-4 h-4" /> {t.cs_add || "Pripojiť obchod"}
        </button>
      </div>

      {/* Empty */}
      {stores.length === 0 && !showAdd && (
        <div className="p-8 text-center bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 rounded-xl" data-testid="cs-empty">
          <Store className="w-12 h-12 text-violet-600 mx-auto mb-3" />
          <h3 className="text-lg font-black text-gray-800 mb-2 font-['Chivo']">
            {t.cs_empty_h || "Žiadne pripojené obchody"}
          </h3>
          <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
            {t.cs_empty_sub || "Pripoj svoj WordPress alebo Shopify a Blog Agent začne automaticky publikovať články do tvojho obchodu."}
          </p>
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <AddStoreForm
          email={email}
          onClose={() => setShowAdd(false)}
          onCreated={async () => { setShowAdd(false); await load(); }}
          t={t}
        />
      )}

      {/* Store cards */}
      <div className="grid gap-3" data-testid="cs-list">
        {stores.map((s) => (
          <div key={s.store_id}
               className="bg-white border border-gray-200 rounded-lg p-4"
               data-testid={`cs-card-${s.store_id}`}>
            <div className="flex items-start gap-3 mb-3">
              {s.platform === "shopify" ? (
                <ShoppingCart className="w-7 h-7 text-green-600 shrink-0" />
              ) : (
                <Globe className="w-7 h-7 text-blue-600 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-gray-800 truncate">{s.store_url}</h3>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                    s.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"
                  }`}>{s.status}</span>
                </div>
                <p className="text-xs text-gray-500 font-mono mt-0.5">
                  {s.platform.toUpperCase()} · {(s.languages || []).join(", ").toUpperCase() || (t.cs_all_langs || "VŠETKY JAZYKY")} · {s.articles_published || 0} {t.cs_published || "publikovaných"}
                </p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => testConn(s.store_id)}
                        disabled={testing === s.store_id}
                        className="p-1.5 text-gray-400 hover:text-violet-600"
                        title={t.cs_test || "Test pripojenia"}
                        data-testid={`cs-test-${s.store_id}`}>
                  {testing === s.store_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </button>
                <button onClick={() => togglePause(s)}
                        className="p-1.5 text-gray-400 hover:text-amber-600 text-xs font-bold"
                        data-testid={`cs-pause-${s.store_id}`}>
                  {s.status === "active" ? "⏸" : "▶"}
                </button>
                <button onClick={() => remove(s.store_id)}
                        className="p-1.5 text-gray-400 hover:text-rose-600"
                        data-testid={`cs-delete-${s.store_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* WP API key */}
            {s.platform === "wordpress" && s.plugin_api_key && (
              <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                  {t.cs_plugin_key || "Plugin API kľúč"} — {t.cs_plugin_key_hint || "vlož do WordPress → TopRankPulse → Settings → Blog Agent"}
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-white border border-gray-200 px-2 py-1.5 rounded truncate">
                    {s.plugin_api_key}
                  </code>
                  <button onClick={() => copy(s.plugin_api_key)}
                          className="p-1.5 bg-white border border-gray-200 rounded hover:bg-gray-100"
                          data-testid={`cs-copy-${s.store_id}`}>
                    {copiedKey === s.plugin_api_key ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                  </button>
                  <button onClick={() => rotateKey(s.store_id)}
                          className="text-[10px] font-bold uppercase tracking-wider text-violet-600 hover:underline px-1"
                          data-testid={`cs-rotate-${s.store_id}`}>
                    {t.cs_rotate || "Rotovať"}
                  </button>
                </div>
              </div>
            )}

            {/* Test result */}
            {testResult[s.store_id] && (
              <div className={`mt-3 px-3 py-2 rounded text-xs flex items-start gap-2 ${
                testResult[s.store_id].ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"
              }`} data-testid={`cs-test-result-${s.store_id}`}>
                {testResult[s.store_id].ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
                <span>
                  {testResult[s.store_id].ok
                    ? (testResult[s.store_id].shop_name
                        ? `${t.cs_test_ok || "OK"} — ${testResult[s.store_id].shop_name} (${testResult[s.store_id].domain})`
                        : testResult[s.store_id].message || (t.cs_test_ok || "OK"))
                    : (testResult[s.store_id].detail || testResult[s.store_id].error || `HTTP ${testResult[s.store_id].status_code}`)}
                </span>
              </div>
            )}

            {/* Last sync */}
            <p className="text-[10px] text-gray-400 mt-3 font-mono">
              {t.cs_last_sync || "Posledná synchronizácia"}: {s.last_sync_at ? new Date(s.last_sync_at).toLocaleString() : (t.cs_never || "nikdy")}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddStoreForm({ email, onClose, onCreated, t }) {
  const [platform, setPlatform] = useState("wordpress");
  const [storeUrl, setStoreUrl] = useState("");
  const [shopifyToken, setShopifyToken] = useState("");
  const [shopifyBlogId, setShopifyBlogId] = useState("");
  const [webflowToken, setWebflowToken] = useState("");
  const [webflowCollectionId, setWebflowCollectionId] = useState("");
  const [wixToken, setWixToken] = useState("");
  const [wixSiteId, setWixSiteId] = useState("");
  const [wixMemberId, setWixMemberId] = useState("");
  const [langs, setLangs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleLang = (code) => {
    setLangs(langs.includes(code) ? langs.filter(l => l !== code) : [...langs, code]);
  };

  const submit = async () => {
    setError("");
    if (!storeUrl) { setError(t.cs_err_url || "Zadaj URL obchodu."); return; }
    if (platform === "shopify" && !shopifyToken) {
      setError(t.cs_err_token || "Pre Shopify potrebuješ Admin API token."); return;
    }
    if (platform === "webflow" && (!webflowToken || !webflowCollectionId)) {
      setError(t.cs_err_webflow || "Pre Webflow potrebuješ API token a Collection ID."); return;
    }
    if (platform === "wix" && (!wixToken || !wixSiteId || !wixMemberId)) {
      setError(t.cs_err_wix || "Pre Wix potrebuješ API token, Site ID a Member ID."); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/connected-stores`, {
        email,
        platform,
        store_url: storeUrl,
        languages: langs,
        shopify_admin_token: shopifyToken || undefined,
        shopify_blog_id: shopifyBlogId || undefined,
        webflow_api_token: webflowToken || undefined,
        webflow_collection_id: webflowCollectionId || undefined,
        wix_api_token: wixToken || undefined,
        wix_site_id: wixSiteId || undefined,
        wix_member_id: wixMemberId || undefined,
      });
      await onCreated();
    } catch (e) {
      setError(e?.response?.data?.detail || (t.cs_err_save || "Uloženie zlyhalo."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border-2 border-violet-200 rounded-lg p-5 space-y-4" data-testid="cs-add-form">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-black text-gray-800 font-['Chivo']">
          {t.cs_add_h || "Pripojiť nový obchod"}
        </h3>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" data-testid="cs-close-form">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div>
        <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t.cs_platform || "Platforma"}</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          {[
            { code: "wordpress", label: "WordPress", icon: Globe, color: "blue" },
            { code: "shopify",   label: "Shopify",   icon: ShoppingCart, color: "green" },
            { code: "webflow",   label: "Webflow",   icon: Globe, color: "indigo" },
            { code: "wix",       label: "Wix",       icon: Globe, color: "violet" },
          ].map(p => (
            <button key={p.code}
                    onClick={() => setPlatform(p.code)}
                    className={`p-3 rounded-lg border-2 flex items-center gap-2 ${
                      platform === p.code ? `border-${p.color}-500 bg-${p.color}-50` : "border-gray-200"
                    }`}
                    data-testid={`cs-platform-${p.code}`}>
              <p.icon className={`w-5 h-5 text-${p.color}-600`} />
              <span className="font-bold text-sm">{p.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t.cs_store_url || "URL obchodu"}</label>
        <input type="text"
               value={storeUrl}
               onChange={(e) => setStoreUrl(e.target.value)}
               placeholder={platform === "shopify" ? "https://yourshop.myshopify.com" : "https://yourblog.com"}
               className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
               data-testid="cs-input-url" />
      </div>

      {platform === "shopify" && (
        <>
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t.cs_shopify_token || "Shopify Admin API token"}</label>
            <input type="password"
                   value={shopifyToken}
                   onChange={(e) => setShopifyToken(e.target.value)}
                   placeholder="shpat_..."
                   className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                   data-testid="cs-input-token" />
            <p className="text-[10px] text-gray-400 mt-1">
              Shopify admin → Settings → Apps → Develop apps → Create app → API credentials → Admin API access token. {t.cs_shopify_scope || "Required scope"}: write_content.
            </p>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
              {t.cs_blog_id || "Blog ID"} <span className="text-gray-400 normal-case font-normal">({t.cs_optional || "voliteľné — auto-detekcia ak prázdne"})</span>
            </label>
            <input type="text"
                   value={shopifyBlogId}
                   onChange={(e) => setShopifyBlogId(e.target.value)}
                   placeholder="79401582788"
                   className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                   data-testid="cs-input-blog-id" />
          </div>
        </>
      )}

      {platform === "webflow" && (
        <>
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t.cs_webflow_token || "Webflow API token"}</label>
            <input type="password"
                   value={webflowToken}
                   onChange={(e) => setWebflowToken(e.target.value)}
                   placeholder="••••••••••••••••"
                   className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                   data-testid="cs-input-webflow-token" />
            <p className="text-[10px] text-gray-400 mt-1">
              Webflow Site Settings → Apps &amp; integrations → API access → Generate token. {t.cs_webflow_scope || "Scope"}: CMS:write.
            </p>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t.cs_webflow_collection || "Collection ID"}</label>
            <input type="text"
                   value={webflowCollectionId}
                   onChange={(e) => setWebflowCollectionId(e.target.value)}
                   placeholder="6516..."
                   className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                   data-testid="cs-input-webflow-collection" />
            <p className="text-[10px] text-gray-400 mt-1">
              Webflow Designer → CMS → tvoja Blog Posts collection → Settings → Collection ID
            </p>
          </div>
        </>
      )}

      {platform === "wix" && (
        <>
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t.cs_wix_token || "Wix API token"}</label>
            <input type="password"
                   value={wixToken}
                   onChange={(e) => setWixToken(e.target.value)}
                   placeholder="••••••••••••••••"
                   className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                   data-testid="cs-input-wix-token" />
            <p className="text-[10px] text-gray-400 mt-1">
              Wix Dashboard → Settings → Headless Settings → API Keys → Generate API Key. {t.cs_wix_scope || "Permissions"}: Blog API.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t.cs_wix_site || "Site ID"}</label>
              <input type="text"
                     value={wixSiteId}
                     onChange={(e) => setWixSiteId(e.target.value)}
                     placeholder="00000000-0000-..."
                     className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                     data-testid="cs-input-wix-site" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t.cs_wix_member || "Member ID"}</label>
              <input type="text"
                     value={wixMemberId}
                     onChange={(e) => setWixMemberId(e.target.value)}
                     placeholder="0000..."
                     className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                     data-testid="cs-input-wix-member" />
            </div>
          </div>
          <p className="text-[10px] text-gray-400 -mt-2">
            ℹ️ {t.cs_wix_note || "Wix publikuje ako DRAFT (bezpečnostná politika Wix-u) — schváliš v Wix admin pred uverejnením."}
          </p>
        </>
      )}

      <div>
        <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
          {t.cs_languages || "Jazyky na publikovanie"} <span className="text-gray-400 normal-case font-normal">({t.cs_lang_hint || "prázdne = všetky"})</span>
        </label>
        <div className="flex gap-1 flex-wrap mt-2">
          {ALL_LANGS.map(([code, label]) => (
            <button key={code}
                    onClick={() => toggleLang(code)}
                    className={`px-2 py-1 rounded text-xs font-bold ${
                      langs.includes(code) ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-700"
                    }`}
                    data-testid={`cs-lang-${code}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded px-3 py-2" data-testid="cs-error">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button onClick={submit}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold px-4 py-2 rounded disabled:opacity-60 flex items-center gap-2"
                data-testid="cs-submit">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {saving ? (t.cs_saving || "Ukladám…") : (t.cs_create || "Pripojiť")}
        </button>
        <button onClick={onClose}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold px-4 py-2 rounded"
                data-testid="cs-cancel">
          {t.cs_cancel || "Zrušiť"}
        </button>
      </div>
    </div>
  );
}
